import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import { useEffect } from 'react';
import ScrollBox from '@erikdakoda/ui/components/ScrollBox';
import SearchBox from '@erikdakoda/ui/components/SearchBox';
import SidebarToolbar from '@erikdakoda/ui/components/SidebarToolbar';
import { useFindManyArtifact } from '@erikdakoda/database/hooks/artifact';
import { useCurrentUser } from '@erikdakoda/ui/hooks/useCurrentUser';
import { ArtifactForSidebarSelect, type ArtifactForSidebar } from '@erikdakoda/artifact/types';
import { useArtifactsSidebarStore } from '../hooks/useArtifactsSidebarStore';
import ArtifactListItem from './ArtifactListItem';

/**
 * Left drawer sidebar listing owner-scoped persisted artifacts.
 */
export default function ArtifactsSidebar() {
  'use memo';

  const { user } = useCurrentUser();
  const artifactFilter = useArtifactsSidebarStore((state) => state.artifactFilter);
  const setArtifactFilter = useArtifactsSidebarStore((state) => state.setArtifactFilter);
  const artifactsRefreshToken = useArtifactsSidebarStore((state) => state.artifactsRefreshToken);

  const {
    data: artifacts,
    isLoading,
    refetch,
  } = useFindManyArtifact(
    {
      where: { ownerId: user?.id },
      orderBy: [{ updatedAt: 'desc' }],
      select: ArtifactForSidebarSelect,
    },
    { enabled: Boolean(user?.id) },
  );

  useEffect(() => {
    if (artifactsRefreshToken === 0) {
      return;
    }
    void refetch();
  }, [artifactsRefreshToken, refetch]);

  const normalizedFilter = artifactFilter.trim().toLowerCase();
  const filteredArtifacts = (artifacts ?? []).filter((artifact: ArtifactForSidebar) =>
    normalizedFilter ? artifact.name.toLowerCase().includes(normalizedFilter) : true,
  );

  return (
    <Box
      className="artifacts-sidebar"
      sx={{ height: '100%', display: 'grid', gridTemplateRows: 'min-content 1fr' }}
    >
      <SidebarToolbar
        title="Artifacts"
        icon="layer-group"
        refetch={async () => {
          await refetch();
        }}
        searchBox={<SearchBox setFilter={setArtifactFilter} defaultFilter={artifactFilter} />}
        searchValue={artifactFilter}
      />
      <ScrollBox disableTopFade overflowXHidden>
        {isLoading ? null : filteredArtifacts.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              px: 2,
              py: 2,
            }}
          >
            {normalizedFilter ? 'No artifacts match your search.' : 'No artifacts yet.'}
          </Typography>
        ) : (
          <List dense disablePadding>
            {filteredArtifacts.map((artifact) => (
              <ArtifactListItem
                key={artifact.id}
                artifact={artifact}
                onChanged={() => {
                  void refetch();
                }}
              />
            ))}
          </List>
        )}
      </ScrollBox>
    </Box>
  );
}
